import Axios from "axios";

export interface TtsGenerationRequest {
  languageId: string;
  voiceName: string;
  text: string;
  ssml?: string;
  isRawSsml?: boolean;
  breakTime?: number;
  prosody?: number;
}

// https://github.com/duolingo/tts-generation-backend/blob/master/application/models/age.py
export const TTS_GENERATION_AGES = [
  "child",
  "teen",
  "adult",
  "mature",
  "elderly",
] as const;
export type TtsGenerationAge = (typeof TTS_GENERATION_AGES)[number];

// https://github.com/duolingo/tts-generation-backend/blob/master/application/models/character.py
export const TTS_GENERATION_CHARACTERS = [
  "bea",
  "eddy",
  "junior",
  "falstaff",
  "lily",
  "lin",
  "lucy",
  "oscar",
  "vikram",
  "zari",
] as const;
export type TtsGenerationCharacter = (typeof TTS_GENERATION_CHARACTERS)[number];

// https://github.com/duolingo/tts-generation-backend/blob/master/application/models/gender.py
export const TTS_GENERATION_GENDERS = ["male", "female", "other"] as const;
export type TtsGenerationGender = (typeof TTS_GENERATION_GENDERS)[number];

// https://github.com/duolingo/tts-generation-backend/blob/master/application/models/provider.py
export const TTS_GENERATION_PROVIDERS = [
  "azure",
  "elevenlabs",
  "google",
  "amazon",
  "openai",
] as const;
export type TtsGenerationProvider = (typeof TTS_GENERATION_PROVIDERS)[number];

export type TtsGenerationSpeed = "slow" | "normal" | "fast";

// https:// github.com/duolingo/tts-generation-backend/blob/master/application/models/voice_status.py
export const TTS_GENERATION_STATUSES = [
  "draft",
  "active",
  "deprecated",
  "unavailable",
] as const;
export type TtsGenerationStatus = (typeof TTS_GENERATION_STATUSES)[number];

export type TtsGenerationCustomVoiceEndpointStatus =
  | "SUSPENDED"
  | "SPINNING_DOWN"
  | "SPINNING_UP"
  | "ACTIVE"
  | "UNKNOWN";

const ttsGenerationClient = Axios.create({
  baseURL: `${process.env.REACT_APP_TTS_GENERATION_ORIGIN}`,
  withCredentials: true,
});

export type CreateVoicePayload = Omit<VoicesV2Item, "added_at" | "id"> & {
  is_default_version: true;
  version: 1;
};

export type UpdateVoicePayload = Partial<
  Omit<VoicesV2Item, "added_at" | "added_by" | "id">
> & {
  last_updated_by: string;
};

// v2 voices API response item (subset of fields we need)
export interface VoicesV2Item {
  added_at: string | null;
  added_by: string;
  age: TtsGenerationAge | null;
  friendly_name: string | null;
  id: string;
  is_always_available?: boolean;
  is_default_version: boolean;
  is_custom_voice?: boolean;
  is_expressive?: boolean;
  is_multilingual?: boolean;
  language_statuses: { language: string; qa_status: string }[];
  last_updated_at: string | null;
  last_updated_by: string;
  name: string;
  notes: string;
  gender: TtsGenerationGender | null;
  primary_language: string | null;
  provider: TtsGenerationProvider;
  provider_parameters: {
    model_name: string;
    pitch?: number | null;
    rate?: number | null;
    similarity_boost?: number | null;
    stability?: number | null;
    style?: number | null;
    use_speaker_boost?: boolean | null;
    volume?: number | null;
  };
  speed: TtsGenerationSpeed | null;
  status: TtsGenerationStatus;
  tags: string[];
  use_cases: string[];
  version: number;
  world_character: TtsGenerationCharacter | null;
}

const getTtsGenerationVoices = async () => {
  const v2 = (await ttsGenerationClient.get<VoicesV2Item[]>("/v2/voices")).data;
  return Object.fromEntries(v2.map(voice => [voice.name, voice]));
};

let ttsGenerationVoicesMemo: Record<string, VoicesV2Item>;
export const getTtsGenerationVoiceList = async () => {
  if (!ttsGenerationVoicesMemo) {
    ttsGenerationVoicesMemo = await getTtsGenerationVoices();
  }

  return Object.values(ttsGenerationVoicesMemo);
};

export const getTtsGenerationVoice = async (
  voiceName: string,
): Promise<VoicesV2Item | undefined> => {
  if (!ttsGenerationVoicesMemo) {
    ttsGenerationVoicesMemo = await getTtsGenerationVoices();
  }

  return ttsGenerationVoicesMemo[voiceName];
};

export const getLanguages = async () =>
  (
    await ttsGenerationClient.get<
      {
        bcp47: string;
        default_dialect: boolean;
        duo_code: string;
        duo_id: string;
        example_sentence: string;
        iso1: string;
        iso3: string;
        name: string;
        scripts: string[];
        whitespace_delimited: boolean;
      }[]
    >("/v1/languages")
  ).data;

export const getCustomVoiceEndpointStatus = async (voiceName: string) =>
  (await getCustomVoiceEndpointStatuses([voiceName]))[voiceName];

export const getCustomVoiceEndpointStatuses = async (voiceNames: string[]) =>
  (
    await ttsGenerationClient.get<
      Record<string, TtsGenerationCustomVoiceEndpointStatus>
    >("/endpoint/statuses", {
      params: {
        voiceNames: voiceNames.join(","),
      },
    })
  ).data;

export const spinUpCustomVoiceEndpoint = (voiceName: string) =>
  spinUpCustomVoiceEndpoints([voiceName]);

export const spinUpCustomVoiceEndpoints = (voiceNames: string[]) =>
  ttsGenerationClient.post<"Accepted">(
    "/endpoint/spin-up",
    {
      voiceNames,
    },
    { validateStatus: status => status === 202 },
  );

export const getTtsGenerationAudio = async (
  requestData: TtsGenerationRequest,
) =>
  (
    await ttsGenerationClient.post<Blob>(
      "/tts/sync/file",
      {
        language_id: requestData.languageId,
        texts: [
          {
            break_time: requestData.breakTime,
            content: requestData.text,

            prosody_rate: requestData.prosody,
            raw_ssml: requestData.isRawSsml ? requestData.ssml : undefined,
            ssml_body: requestData.isRawSsml ? undefined : requestData.ssml,
          },
        ],
        voice_names: [requestData.voiceName],
      },
      {
        headers: {
          Accept: "application/json",
        },
        responseType: "blob",
      },
    )
  ).data;

export const createVoice = async (payload: CreateVoicePayload) =>
  (await ttsGenerationClient.post<VoicesV2Item>("/voices", payload)).data;

export const patchVoice = async (id: string, payload: UpdateVoicePayload) =>
  (await ttsGenerationClient.patch<VoicesV2Item>(`/voices/${id}`, payload))
    .data;
